import { useAuth0 } from '@auth0/auth0-react';
import { type FormEvent, useEffect, useState } from 'react';

import { ApiError, useAuth } from '../auth/AuthProvider';

export function LoginPage() {
  const {
    jwt,
    mockEnabled,
    loginWithEmail,
    loginWithAuth0,
    completeAuth0Exchange,
  } = useAuth();
  const auth0 = useAuth0();
  const [email, setEmail] = useState('owner@example.com');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Real Auth0 path: once the Auth0 SDK has finished its callback dance and
  // the user is authenticated, exchange the Auth0 access token for a
  // Signatory JWT. This effect is a no-op under mock mode.
  useEffect(() => {
    if (mockEnabled) return;
    if (auth0.isLoading) return;
    if (!auth0.isAuthenticated) return;
    if (jwt) return; // already exchanged — don't re-run
    let cancelled = false;
    (async () => {
      try {
        await completeAuth0Exchange();
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `${err.code ?? err.status}: ${err.message}`
            : err instanceof Error
              ? err.message
              : 'unknown error',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    mockEnabled,
    auth0.isLoading,
    auth0.isAuthenticated,
    completeAuth0Exchange,
    jwt,
  ]);

  async function onMockSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await loginWithEmail(email);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code ?? err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'unknown error',
      );
    } finally {
      setPending(false);
    }
  }

  async function onAuth0Click() {
    setError(null);
    setPending(true);
    try {
      await loginWithAuth0();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown error');
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="card w-full max-w-md">
        <h1 className="mb-1 text-xl font-semibold text-brand">Sign in</h1>
        <p className="mb-4 text-sm text-slate-600">
          {mockEnabled
            ? 'Dev login via mock Auth0.'
            : 'Sign in with Auth0 to continue.'}
        </p>
        {mockEnabled ? (
          <form onSubmit={onMockSubmit} className="space-y-3">
            <div>
              <label className="label" htmlFor="login-email">
                Email
              </label>
              <input
                id="login-email"
                className="input mt-1"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={pending}
            >
              {pending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <button
              type="button"
              onClick={onAuth0Click}
              className="btn-primary w-full"
              disabled={pending || auth0.isLoading}
            >
              {pending || auth0.isLoading ? 'Redirecting…' : 'Sign in with Auth0'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
